import React, { createContext, useContext, ReactNode, useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useSwivuChecklist, TUseSwivuChecklistReturn } from "@/react/hooks/entityHooks/useSwivuChecklist";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { EntityRecord, WidgetParamKeys } from "@/types/entity";
import { EDITOR_KEYS } from "@/other/constants/EditorKeys";
import { toast } from "sonner";
import { CHECKLIST_ITEM_STATUS_TYPE } from "@/other/constants/OtherKeys.jsx";
import {
  isChecklistItemChecked,
  isDraftId,
  type Checklist,
  type ChecklistItem
} from "../types";
import {
  invalidateChecklistDetail,
  seedChecklistDetail,
  setChecklistDetailItems,
  useChecklistDetailQuery,
  useCreateChecklistMutation,
  useCreateChecklistItemMutation,
  useDeleteChecklistItemMutation,
  usePatchChecklistMutation,
  usePatchChecklistItemMutation,
  useRemoveChecklistItemImageMutation,
  useUploadChecklistItemImageMutation
} from "../api/checklist.api";
import { checklistEntityPathType } from "../api/checklist.query-keys";
import type { ChecklistRecordWriteResponse, PatchChecklistItemPayload, PatchChecklistPayload } from "../api/checklist.types";
import { mergeChecklistRecordWrite } from "../utils/checklistApiAdapters";

const ALLOWED_IMAGE_TYPES = ["png", "jpeg", "jpg", "svg", "heif"];

interface EntityChecklistContextValue extends Omit<TUseSwivuChecklistReturn, "updateChecklist"> {
  /** Resolves after the save when the edit is persisted over REST, so callers can wait for it. */
  updateChecklist: (checklist: Checklist, updates: Partial<Checklist>) => boolean | Promise<boolean>;
  selectedChecklist: Checklist | null;
  newChecklist: Checklist | null;
  selectedChecklistItems: ChecklistItem[];
  isCreatingNewChecklist: boolean;
  isLoadingChecklist: boolean;
  handleShowChecklistDetails: (checklist: Checklist) => void;
  handleHideChecklistDetails: () => void;
  handleCloseEditor: () => void;
  persistNewChecklist: () => Promise<void>;
  hasChecklists: boolean;
  handleOpenEditor: (isCreatingNew: boolean, checklist?: Checklist) => void;
  canEditSelectedChecklist: boolean;
  createChecklistItem: (checklist: Checklist, description?: string) => void;
  updateChecklistItem: (item: ChecklistItem, updates: Partial<ChecklistItem>) => boolean;
  deleteChecklistItem: (item: ChecklistItem) => boolean;
  toggleChecklistItem: (item: ChecklistItem) => boolean;
  reorderChecklistItems: (items: ChecklistItem[]) => boolean;
  uploadChecklistItemImage: (item: ChecklistItem, file: File) => Promise<boolean | null>;
  removeChecklistItemImage: (item: ChecklistItem) => Promise<boolean | null>;
}

const EntityChecklistContext = createContext<EntityChecklistContextValue | undefined>(undefined);

interface EntityChecklistProviderProps {
  children: ReactNode;
  /** Null for board-scoped checklists with no task/resource/client (edit by id only). */
  record: EntityRecord | null;
  initialChecklistId?: string | number;
  isCreatingNew?: boolean;
  assignedBoardUserIds?: number[];
  /** Grants from checkgrids list when retrieve omits them. */
  canEditGrant?: number;
  canViewGrant?: number;
  onCloseEditor?: () => void;
}

function readBoardId(selectedBoard: unknown): number | null {
  const board = selectedBoard as { id?: number; record?: { id?: number } } | null;
  return board?.id ?? board?.record?.id ?? null;
}

const NO_ITEMS: ChecklistItem[] = [];

function sameChecklistId(a: string | number | null | undefined, b: string | number | null | undefined): boolean {
  if (a == null || b == null) return false;
  return String(a) === String(b);
}

export const EntityChecklistProvider: React.FC<EntityChecklistProviderProps> = ({
  children,
  record,
  initialChecklistId,
  isCreatingNew = false,
  assignedBoardUserIds,
  canEditGrant,
  canViewGrant,
  onCloseEditor
}) => {
  const { editorHandler, user, entityManager, selectedBoard } = useSwivuBryntumContext();
  const { callParamAction } = entityManager;
  const checklistOperations = useSwivuChecklist({ record });
  const { checklists, refreshChecklists, isLoadingChecklists, hasNextPage } = checklistOperations;
  const [selectedChecklist, setSelectedChecklist] = useState<Checklist | null>(null);
  const [detailChecklistId, setDetailChecklistId] = useState<string | number | null>(
    initialChecklistId && !isCreatingNew ? initialChecklistId : null
  );
  const [isCreatingNewChecklist, setIsCreatingNewChecklist] = useState(false);
  const [newChecklist, setNewChecklist] = useState<Checklist | null>(null);
  const boardId = readBoardId(selectedBoard);
  const createChecklistMutation = useCreateChecklistMutation();
  const patchChecklistMutation = usePatchChecklistMutation();
  const createChecklistItemMutation = useCreateChecklistItemMutation();
  const patchChecklistItemMutation = usePatchChecklistItemMutation();
  const deleteChecklistItemMutation = useDeleteChecklistItemMutation();
  const uploadChecklistItemImageMutation = useUploadChecklistItemImageMutation();
  const removeChecklistItemImageMutation = useRemoveChecklistItemImageMutation();
  const entityFk = checklistEntityPathType(record?.entityType);
  const entityId = record?.isPhantom ? null : record?.id;

  // After delete, list cache drops the row before detail is removed. Disable
  // retrieve while the id is gone so tile+editor observers do not 404-refetch.
  // The entity list only proves a checklist is gone when the editor is scoped to an entity and every
  // page is loaded; board-only checklists (opened from Checklists with no host) never appear in it.
  const entityListIsAuthoritative = Boolean(entityFk && entityId != null) && !hasNextPage;
  const detailExistsInList =
    detailChecklistId == null ||
    isDraftId(detailChecklistId) ||
    !entityListIsAuthoritative ||
    isLoadingChecklists ||
    checklists.some((row) => sameChecklistId(row.id, detailChecklistId));

  // isCreatingNew stays true for the whole create-editor session; only block
  // detail while the draft (newChecklist) is still unsaved.
  const detailQuery = useChecklistDetailQuery({
    boardId,
    checklistId: detailChecklistId,
    assignedBoardUserIds,
    canEditGrant,
    canViewGrant,
    enabled:
      detailChecklistId != null &&
      detailExistsInList &&
      !(isCreatingNew && newChecklist != null)
  });

  // Clear local selection when the open checklist disappears from the list (delete).
  // Tile + editor are separate providers sharing list cache — this is how the tile
  // leaves detail view after a delete in the editor.
  useEffect(() => {
    if (detailChecklistId == null || isDraftId(detailChecklistId)) return;
    if (!entityListIsAuthoritative || isLoadingChecklists) return;
    if (checklists.some((row) => sameChecklistId(row.id, detailChecklistId))) return;
    setSelectedChecklist(null);
    setDetailChecklistId(null);
  }, [checklists, detailChecklistId, isLoadingChecklists, entityListIsAuthoritative]);

  // Only react to a *changed* prop. On mount the id is already in state; clearing here would wipe the
  // cache-seeded items (effect order) and the seed effect would never re-run for the same cached data.
  // Skip re-applying when that row was removed (delete).
  const prevInitialChecklistIdRef = useRef(initialChecklistId);
  useEffect(() => {
    if (prevInitialChecklistIdRef.current === initialChecklistId) return;
    prevInitialChecklistIdRef.current = initialChecklistId;
    if (!initialChecklistId || isCreatingNew) return;
    if (
      entityListIsAuthoritative &&
      !isLoadingChecklists &&
      !checklists.some((row) => sameChecklistId(row.id, initialChecklistId))
    ) {
      return;
    }
    setDetailChecklistId(initialChecklistId);
    setSelectedChecklist(null);
  }, [initialChecklistId, isCreatingNew, checklists, isLoadingChecklists, entityListIsAuthoritative]);

  useEffect(() => {
    if (isCreatingNew) {
      const created = checklistOperations.createChecklist("", []);
      setNewChecklist(created);
      setDetailChecklistId(null);
    }
  }, [isCreatingNew]);

  // The editor shows the detail query's items directly; only the checklist row is mirrored into state
  // (selection can also come from the list before retrieve lands).
  const detailData =
    detailQuery.data && sameChecklistId(detailQuery.data.checklist.id, detailChecklistId) ? detailQuery.data : undefined;

  const detailChecklist = detailData?.checklist;
  useEffect(() => {
    if (detailChecklist) setSelectedChecklist(detailChecklist);
  }, [detailChecklist]);

  const isLoadingChecklist =
    detailChecklistId != null && (detailQuery.isPending || detailQuery.isFetching) && detailData == null;

  const persistNewChecklist = async () => {
    if (!newChecklist || isCreatingNewChecklist || !boardId) return;
    if (!record || record.id == null || record.isPhantom) {
      toast.error("This checklist needs a task, resource, or client before it can be created.");
      return;
    }
    setIsCreatingNewChecklist(true);
    if (newChecklist.title.trim() === "") newChecklist.title = "New Checklist";
    try {
      // Reuse the id if an earlier attempt already created the checklist.
      let checklistId: number | string | null = !isDraftId(newChecklist.id) ? newChecklist.id : null;
      let writeResponse: ChecklistRecordWriteResponse | null = null;
      if (checklistId == null) {
        const entityFk = checklistEntityPathType(record.entityType);
        if (!entityFk) throw new Error("unsupported entity type");
        // Leave Can View / Can Edit off the body so create applies the saved defaults.
        const created = await createChecklistMutation.mutateAsync({
          boardId,
          entityType: entityFk,
          entityId: record.id,
          body: {
            title: newChecklist.title,
            [entityFk]: record.id,
            archived: Boolean(newChecklist.archived),
            recurrence: newChecklist.recurrence ?? "none",
            assigned_boardusers: newChecklist.assignedBoardUsers ?? []
          }
        });
        if (created?.id == null) throw new Error("missing id");
        checklistId = created.id;
        writeResponse = created;
        newChecklist.id = created.id;
      }
      if (checklistId == null) throw new Error("missing id");

      // Keep the saved title, and copy created/updated/creator/updater and the
      // saved Can View / Can Edit from the write response. The draft has none of
      // the timestamps, and the detail cache is what the editor reads again when
      // Manage is reopened without a page reload.
      const draftChecklist = { ...newChecklist, id: checklistId };
      const persistedChecklist = writeResponse
        ? mergeChecklistRecordWrite(draftChecklist, writeResponse)
        : draftChecklist;
      setSelectedChecklist(persistedChecklist);
      seedChecklistDetail(boardId, { checklist: persistedChecklist, items: [] });
      setNewChecklist(null);
      await refreshChecklists();
      setDetailChecklistId(checklistId);
    } catch {
      toast.error("Unable to create checklist.");
    } finally {
      setIsCreatingNewChecklist(false);
    }
  };

  const handleShowChecklistDetails = useCallback((checklist: Checklist) => {
    setSelectedChecklist(checklist);
    if (checklist.id != null && !isDraftId(checklist.id)) {
      setDetailChecklistId(checklist.id);
    } else {
      setDetailChecklistId(null);
    }
  }, []);

  const handleHideChecklistDetails = () => {
    setSelectedChecklist(null);
    setDetailChecklistId(null);
  };

  const handleCloseEditor = () => {
    onCloseEditor?.();
  };

  const handleOpenEditor = useCallback(
    (creating: boolean, checklist?: Checklist) => {
      editorHandler.openEditor(
        EDITOR_KEYS.checklistEditor,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        null,
        {
          entity: record,
          checklistId: checklist?.id || selectedChecklist?.id,
          isCreatingNew: creating
        },
        { by: "recordType", useExisting: true }
      );
    },
    [editorHandler, record, selectedChecklist?.id]
  );

  const selectedChecklistItems = detailData?.items ?? NO_ITEMS;

  // RQ list rows get a new `checklists` array as pages resolve; CHECKLIST.EDIT clear is
  // debounced (~1s). Without this guard the deeplink effect re-opens the editor every time.
  const handledChecklistEditParamRef = useRef<string | null>(null);

  useEffect(() => {
    callParamAction(WidgetParamKeys.CHECKLIST.EDIT, (checklistId) => {
      const key = String(checklistId);
      if (handledChecklistEditParamRef.current === key) return true;
      const checklist = checklists.find((c) => sameChecklistId(c.id, checklistId));
      if (!checklist) return false;
      handledChecklistEditParamRef.current = key;
      handleOpenEditor(false, checklist);
      return true;
    });
  }, [callParamAction, checklists, handleOpenEditor]);

  const canEditSelectedChecklist = useMemo(() => {
    if (!selectedChecklist || !user) return false;
    if (checklistOperations.canEditChecklist(selectedChecklist)) return true;
    if (canEditGrant != null && user.can(canEditGrant)) return true;
    return false;
  }, [selectedChecklist, user, checklistOperations, canEditGrant]);

  const updateChecklist = useCallback(
    (checklist: Checklist, updates: Partial<Checklist>) => {
      // Draft create: local-only until persist. Must replace state — in-place
      // Object.assign alone does not re-render controlled fields (e.g. recurrence).
      if (newChecklist && sameChecklistId(checklist.id, newChecklist.id)) {
        if (!checklistOperations.updateChecklist(checklist, updates)) return false;
        setNewChecklist({ ...checklist });
        return true;
      }

      if (canEditSelectedChecklist) {
        if (boardId && checklist.id != null && !isDraftId(checklist.id)) {
          const body: PatchChecklistPayload = {};
          if (updates.title !== undefined) body.title = updates.title;
          if (updates.recurrence !== undefined) body.recurrence = updates.recurrence;
          if (updates.recurrenceWeekdays !== undefined) body.recurrence_weekdays = updates.recurrenceWeekdays;
          if (updates.assignedBoardUsers !== undefined) body.assigned_boardusers = updates.assignedBoardUsers;
          if (updates.canEdit !== undefined) body.can_edit_grant = updates.canEdit;
          if (updates.canView !== undefined) body.can_view_grant = updates.canView;
          if (Object.keys(body).length) {
            return patchChecklistMutation
              .mutateAsync({
                boardId,
                checklistId: checklist.id,
                body,
                entityType: entityFk,
                entityId
              })
              .then(() => true)
              .catch(() => {
                toast.error("Unable to save checklist.");
                return false;
              });
          }
        }
        return true;
      }
      return checklistOperations.updateChecklist(checklist, updates);
    },
    [
      newChecklist,
      canEditSelectedChecklist,
      boardId,
      checklistOperations,
      patchChecklistMutation,
      entityFk,
      entityId
    ]
  );

  const canTickItems =
    canEditSelectedChecklist ||
    (selectedChecklist ? checklistOperations.isUserAssignedToChecklist(selectedChecklist) : false);

  const updateChecklistItem = useCallback(
    (checklistItem: ChecklistItem, updates: Partial<ChecklistItem>) => {
      const assigned = selectedChecklist
        ? checklistOperations.isUserAssignedToChecklist(selectedChecklist)
        : false;
      const commentOnly = updates.comment !== undefined
        && updates.description === undefined
        && updates.order === undefined;
      if (!canEditSelectedChecklist && !(assigned && commentOnly)) {
        toast("You don't have permission to edit this checklist item");
        return false;
      }
      if (!boardId || isDraftId(checklistItem.id)) return false;
      const body: PatchChecklistItemPayload = {};
      if (updates.description !== undefined) body.description = updates.description;
      if (updates.comment !== undefined) body.comment = updates.comment;
      if (updates.order !== undefined) body.order = updates.order;
      if (Object.keys(body).length) {
        void patchChecklistItemMutation
          .mutateAsync({
            boardId,
            itemId: checklistItem.id,
            checklistId: checklistItem.checklistId ?? selectedChecklist?.id ?? detailChecklistId,
            entityType: entityFk,
            entityId,
            body
          })
          .catch(() => toast.error("Unable to save checklist item."));
      }
      return true;
    },
    [
      canEditSelectedChecklist,
      boardId,
      checklistOperations,
      patchChecklistItemMutation,
      selectedChecklist,
      detailChecklistId,
      entityFk,
      entityId
    ]
  );

  const deleteChecklistItem = useCallback(
    (checklistItem: ChecklistItem) => {
      if (!canEditSelectedChecklist) {
        toast("You don't have permission to delete this checklist item");
        return false;
      }
      if (!boardId || isDraftId(checklistItem.id)) return false;
      void deleteChecklistItemMutation
        .mutateAsync({
          boardId,
          itemId: checklistItem.id,
          checklistId: checklistItem.checklistId ?? selectedChecklist?.id ?? detailChecklistId,
          entityType: entityFk,
          entityId
        })
        .catch(() => toast.error("Unable to delete checklist item."));
      return true;
    },
    [
      canEditSelectedChecklist,
      boardId,
      deleteChecklistItemMutation,
      selectedChecklist?.id,
      detailChecklistId,
      entityFk,
      entityId
    ]
  );

  const createChecklistItem = useCallback(
    (checklist: Checklist, description: string = "") => {
      if (!boardId || checklist.id == null || isDraftId(checklist.id)) return;
      void createChecklistItemMutation
        .mutateAsync({
          boardId,
          checklistId: checklist.id,
          entityType: entityFk,
          entityId,
          body: { description, order: detailData?.items.length ?? 0 }
        })
        .catch(() => toast.error("Unable to add checklist item."));
    },
    [boardId, createChecklistItemMutation, entityFk, entityId, detailData?.items.length]
  );

  const reorderChecklistItems = useCallback(
    (reorderedItems: ChecklistItem[]) => {
      if (!reorderedItems.length) return false;
      if (!canEditSelectedChecklist) {
        toast.error("You don't have permission to reorder items in this checklist");
        return false;
      }
      const checklistId = selectedChecklist?.id ?? detailChecklistId;
      if (boardId && checklistId != null) {
        const ordered = reorderedItems.map((item, index) => ({ ...item, order: index }));
        setChecklistDetailItems(boardId, checklistId, () => ordered);
        for (const item of ordered) {
          if (isDraftId(item.id)) continue;
          void patchChecklistItemMutation
            .mutateAsync({
              boardId,
              itemId: item.id,
              checklistId,
              entityType: entityFk,
              entityId,
              body: { order: item.order }
            })
            .catch(() => toast.error("Unable to save item order."));
        }
      }
      return true;
    },
    [canEditSelectedChecklist, boardId, patchChecklistItemMutation, selectedChecklist?.id, detailChecklistId, entityFk, entityId]
  );

  const toggleChecklistItem = useCallback(
    (checklistItem: ChecklistItem) => {
      if (!canTickItems) {
        toast("You don't have permission to modify this checklist item");
        return false;
      }
      if (!boardId || isDraftId(checklistItem.id)) return false;

      void patchChecklistItemMutation
        .mutateAsync({
          boardId,
          itemId: checklistItem.id,
          checklistId: checklistItem.checklistId ?? selectedChecklist?.id ?? detailChecklistId,
          entityType: entityFk,
          entityId,
          body: {
            status: isChecklistItemChecked(checklistItem)
              ? CHECKLIST_ITEM_STATUS_TYPE.NOT_COMPLETED
              : CHECKLIST_ITEM_STATUS_TYPE.COMPLETED
          }
        })
        .catch(() => toast.error("Unable to update checklist item."));
      return true;
    },
    [canTickItems, boardId, patchChecklistItemMutation, selectedChecklist?.id, detailChecklistId, entityFk, entityId]
  );

  const uploadChecklistItemImage = useCallback(
    async (checklistItem: ChecklistItem, file: File) => {
      if (isDraftId(checklistItem.id)) return null;
      const assigned = selectedChecklist
        ? checklistOperations.isUserAssignedToChecklist(selectedChecklist)
        : false;
      if (!canEditSelectedChecklist && !assigned) {
        toast.error("You don't have permission to upload images to this checklist item");
        return false;
      }
      if (!boardId) {
        toast.error("Unable to upload image.");
        return false;
      }
      if (!file) {
        toast.error("No file selected");
        return false;
      }
      const fileType = file.type.split("/")[1];
      if (!file.type.includes("image") || !ALLOWED_IMAGE_TYPES.includes(fileType)) {
        toast.error(`Unsupported File Type. Please upload files with extensions: ${ALLOWED_IMAGE_TYPES.join(", ")}`);
        return false;
      }

      try {
        await uploadChecklistItemImageMutation.mutateAsync({
          boardId,
          itemId: checklistItem.id,
          file,
          checklistId: checklistItem.checklistId ?? selectedChecklist?.id ?? detailChecklistId,
          entityType: entityFk,
          entityId
        });
        toast.success("Image uploaded successfully");
        return true;
      } catch (error) {
        const message = error instanceof Error ? error.message : "Failed to upload image. Please try again.";
        toast.error(message);
        return false;
      }
    },
    [
      boardId,
      canEditSelectedChecklist,
      checklistOperations,
      uploadChecklistItemImageMutation,
      selectedChecklist,
      detailChecklistId,
      entityFk,
      entityId
    ]
  );

  const removeChecklistItemImage = useCallback(
    async (checklistItem: ChecklistItem) => {
      if (isDraftId(checklistItem.id)) return null;
      const assigned = selectedChecklist
        ? checklistOperations.isUserAssignedToChecklist(selectedChecklist)
        : false;
      if (!canEditSelectedChecklist && !assigned) {
        toast.error("You don't have permission to remove this image");
        return false;
      }
      if (!boardId) {
        toast.error("Unable to remove image.");
        return false;
      }

      try {
        await removeChecklistItemImageMutation.mutateAsync({
          boardId,
          itemId: checklistItem.id,
          checklistId: checklistItem.checklistId ?? selectedChecklist?.id ?? detailChecklistId,
          entityType: entityFk,
          entityId
        });
        toast.success("Image removed successfully");
        return true;
      } catch {
        toast.error("Failed to remove image. Please try again.");
        return false;
      }
    },
    [
      boardId,
      canEditSelectedChecklist,
      checklistOperations,
      removeChecklistItemImageMutation,
      selectedChecklist,
      detailChecklistId,
      entityFk,
      entityId
    ]
  );

  const hasChecklists = checklists.length > 0 || Boolean(selectedChecklist || newChecklist);

  // Import replaces items server-side, so the detail cache has to be refetched.
  const importChecklistItems = useCallback(
    async (checklist: Checklist, file: File) => {
      const ok = await checklistOperations.importChecklistItems(checklist, file);
      if (!ok) return false;
      const isOpenChecklist =
        sameChecklistId(checklist.id, detailChecklistId) ||
        sameChecklistId(checklist.id, selectedChecklist?.id);
      if (!isOpenChecklist) return true;
      // Create path leaves detailChecklistId null — arm it so the detail query loads.
      if (checklist.id != null && !isDraftId(checklist.id) && !sameChecklistId(detailChecklistId, checklist.id)) {
        setDetailChecklistId(checklist.id);
      }
      if (boardId && checklist.id != null && !isDraftId(checklist.id)) {
        await invalidateChecklistDetail(boardId, checklist.id);
      }
      return true;
    },
    [checklistOperations, detailChecklistId, selectedChecklist?.id, boardId]
  );

  return (
    <EntityChecklistContext.Provider
      value={{
        ...checklistOperations,
        updateChecklist,
        updateChecklistItem,
        deleteChecklistItem,
        createChecklistItem,
        reorderChecklistItems,
        toggleChecklistItem,
        uploadChecklistItemImage,
        removeChecklistItemImage,
        importChecklistItems,
        selectedChecklist,
        selectedChecklistItems,
        isCreatingNewChecklist,
        isLoadingChecklist,
        handleShowChecklistDetails,
        handleHideChecklistDetails,
        handleCloseEditor,
        persistNewChecklist,
        newChecklist,
        hasChecklists,
        handleOpenEditor,
        canEditSelectedChecklist
      }}
    >
      {children}
    </EntityChecklistContext.Provider>
  );
};

export function useEntityChecklistContext(): EntityChecklistContextValue {
  const context = useContext(EntityChecklistContext);
  if (!context) {
    throw new Error("useEntityChecklistContext must be used within EntityChecklistProvider");
  }
  return context;
}
